import type { AssetSource, MediaFormatFilter, MediaType } from '@mg/shared';
import { cn } from '../../lib/cn';
import { MEDIA_TYPE_FILTERS, SOURCE_FILTERS } from './mediaDisplay';

type SourceFilter = AssetSource | 'all';
type TypeFilter = MediaType | MediaFormatFilter | 'all';

/**
 * Filter chips for the grid: source (All / Uploads / Generations) and type (All / Images / Video,
 * plus the two design-tool formats). Filters apply to the folder and trash views alike.
 */
export function MediaToolbar({
  source,
  onSource,
  type,
  onType,
}: {
  source: SourceFilter;
  onSource: (value: SourceFilter) => void;
  type: TypeFilter;
  onType: (value: TypeFilter) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
      <ChipGroup label="Source">
        {SOURCE_FILTERS.map((f) => (
          <Chip key={f.id} active={source === f.id} onClick={() => onSource(f.id)}>
            {f.label}
          </Chip>
        ))}
      </ChipGroup>
      <ChipGroup label="Type">
        {MEDIA_TYPE_FILTERS.map((f) => (
          <Chip key={f.id} active={type === f.id} onClick={() => onType(f.id)}>
            {f.label}
          </Chip>
        ))}
      </ChipGroup>
    </div>
  );
}

function ChipGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-semibold uppercase tracking-wide text-ink-faint">{label}</span>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'focus-ring rounded-full border px-3 py-1 text-sm font-medium transition-colors',
        active
          ? 'border-ink-ghost bg-tint text-tint-fg'
          : 'border-line-input text-ink-label hover:bg-hover',
      )}
    >
      {children}
    </button>
  );
}
